import { Button, Modal, Space, Tag } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { listReviews, deleteReview, editReview } from "../../common/api/ReviewClient";
import { updateOrderForm } from "../../common/api/OrderFormClient";
import { OrderForm } from "../../types/OrderFormTypes";
import { getUsers } from "../../common/api/UserClient";
import { SortFilterPaged } from "../../common/CommonInterfaces";
import { SortableFilteringTable } from "../../common/components/SortableFilteringTable";
import { FilterGroup, PagerObject, Sort } from "../../types/DataFetchingTypes";
import { DocumentReview, ExpandedDocumentReview, ReviewStatus } from "../../types/ReviewTypes";
import { isOnboarding, isSuperAdmin, User } from "../../types/UserGroup";
import { ReviewStatusToTag } from "./ReviewStatus";
import { useUserState } from "../../common/UserContext";
import { Vendor } from "../../types/VendorTypes";
import { getVendors } from "../../common/api/VendorClient";
import { SimulationScore } from "../dashboard-v2/components/SimulationScore";
import { roundToTwoDecimals } from "../../common/Number";
import { Error } from "../../types/Error";
import { ErrorInfoModal } from "../../common/components/ErrorInfoModal";
import { SvgIcon } from "../../common/components/SvgIcon";
import { ReviewUpdateModal } from "./components/ReviewUpdateModal";
import { OwnerCell } from "../../common/components/OwnerCell";

const pager = {
    filter: [] as FilterGroup[],
    sort: [] as Sort[],
    pager: {
        pagination: {
            current: 1,
            pageSize: 10,
        },
    },
};

interface UserEnrichedDocumentReview extends ExpandedDocumentReview {
    user?: User;
    vendor?: Vendor;
    creator?: User;
    owner?: User;
}
const listReviewsWrapper = async (
    paging: SortFilterPaged<UserEnrichedDocumentReview>,
    vendorKeys?: string[],
    abort?: any
): Promise<PagerObject<UserEnrichedDocumentReview[]>> => {
    const reviews = await listReviews(paging, vendorKeys, abort);

    // Collect reviewer IDs
    const reviewerIds = reviews.data
        .map((d) => d.reviewerId)
        .filter(function (elem, index, self) {
            return elem !== -1 && index === self.indexOf(elem);
        });

    // Collect creator IDs
    const creatorIds = reviews.data
        .map((d) => d.creatorId)
        .filter(function (elem, index, self) {
            return elem !== undefined && elem !== -1 && index === self.indexOf(elem);
        }) as number[];

    // Collect owner IDs
    const ownerIds = reviews.data
        .map((d) => d.ownerId)
        .filter(function (elem, index, self) {
            return elem !== undefined && elem !== -1 && index === self.indexOf(elem);
        }) as number[];

    // Combine unique user IDs for a single API call
    const allUserIds = [...reviewerIds, ...creatorIds, ...ownerIds].filter(
        function (elem, index, self) {
            return index === self.indexOf(elem);
        }
    );
    const users = [] as User[];
    if (allUserIds.length > 0) {
        (await getUsers(allUserIds)).forEach((u) => users.push(u));
    }

    const _vendorKeys = reviews.data
        .map((item) => item.vendorKey)
        .filter((key) => key !== undefined && key !== null && key !== "");
    let vendors = [] as Vendor[];
    if (_vendorKeys.length) {
        vendors = await getVendors(_vendorKeys);
    }

    const data = reviews.data.map((r) => {
        const fusers = users.filter((u) => u.id === r.reviewerId);
        const user = fusers !== undefined && fusers.length > 0 ? fusers[0] : undefined;
        const vendor = vendors.find((item) => item.vendorKey === r.vendorKey);
        const creator = r.creatorId ? users.find((u) => u.id === r.creatorId) : undefined;
        const owner = r.ownerId ? users.find((u) => u.id === r.ownerId) : undefined;

        return {
            ...r,
            user: user,
            vendor,
            creator,
            owner,
        } as UserEnrichedDocumentReview;
    });

    return {
        ...reviews,
        data: data,
    } as PagerObject<UserEnrichedDocumentReview[]>;
};

function getColumns(
    isSuperAdmin: boolean,
    onErrorClick?: (error: Error) => void,
    onDelete?: (serId: string) => void,
    onEdit?: (review: DocumentReview) => void,
    isOnboarding = false,
    onOwnerUpdate?: () => void
): any {
    const columns = [
        {
            title: "File name",
            dataIndex: "document.name",
            key: "name",
            width: "25%",
            render: (value: any, row: UserEnrichedDocumentReview) => {
                return <Link to={`/order/${row.orderFormId}`}>{row.document!.name}</Link>;
            },
        },
        {
            title: "Vendor",
            key: "vendorName",
            render: (_: any, row: UserEnrichedDocumentReview) => {
                return row.vendor?.name;
            },
        },
        {
            title: "Upload date",
            dataIndex: "document.uploadDate",
            key: "uploadDate",
            render: (_: any, row: DocumentReview) => {
                const date = new Date(row.document!.uploadDate);
                const dateStr = `${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}-${date.getFullYear()}`;
                const timeStr = date.toLocaleTimeString("en-US", {
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: true,
                });
                return (
                    <div style={{ whiteSpace: "nowrap" }}>
                        {dateStr}
                        <div style={{ fontSize: "0.75em", color: "#888" }}>{timeStr}</div>
                    </div>
                );
            },
        },
        {
            title: "Owner",
            key: "ownerId",
            render: (_: any, row: UserEnrichedDocumentReview) => {
                return (
                    <OwnerCell
                        ownerId={row.ownerId}
                        ownerName={row.owner?.name}
                        onSave={async (newOwnerId) => {
                            await updateOrderForm({
                                id: row.orderFormId,
                                ownerId: newOwnerId,
                            } as OrderForm);
                        }}
                        onUpdate={onOwnerUpdate}
                    />
                );
            },
        },
        ...(isOnboarding
            ? []
            : [
                  {
                      title: "Contract score",
                      dataIndex: "orderScore",
                      key: "orderScore",
                      render: (value: number, row: UserEnrichedDocumentReview) => {
                          return (
                              <Space>
                                  <Link to={`/order/score/${row.orderFormId}`}>
                                      <SvgIcon name="document" size={12} />
                                  </Link>
                                  <SimulationScore score={roundToTwoDecimals(value)} />
                              </Space>
                          );
                      },
                  },
              ]),
        {
            title: "Reviewer",
            dataIndex: "user",
            key: "reviewerId",
            sorter: true,
            render: (value: any, row: UserEnrichedDocumentReview) => {
                if (row.user !== undefined) {
                    return (
                        <Tag color="default" key={`reviewers-${row.documentSerialId}`}>
                            {row.user!.name}
                        </Tag>
                    );
                }
                return (
                    <Tag color="warning" key={`reviewers-${row.documentSerialId}`}>
                        Unassigned
                    </Tag>
                );
            },
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            filter: true,
            filters: Object.values(ReviewStatus).map((v) => {
                return {
                    text: v,
                    value: v,
                };
            }),
            render: (value: any, row: DocumentReview) => {
                return (
                    <>
                        {ReviewStatusToTag(row.status)}{" "}
                        {isSuperAdmin && row.failedReason && row.status === ReviewStatus.FAILED ? (
                            <ExclamationCircleOutlined
                                style={{ color: "#FF4D4F" }}
                                onClick={() => onErrorClick?.(row.failedReason!)}
                            />
                        ) : null}
                    </>
                );
            },
        },
    ];
    if (isSuperAdmin) {
        columns.push({
            title: "Action",
            dataIndex: "action",
            key: "action",
            render: (value: any, row: DocumentReview) => {
                return (
                    <>
                        <Button
                            type="link"
                            icon={<SvgIcon name="edit" size={12} />}
                            onClick={() => onEdit?.(row)}
                        ></Button>

                        <Button
                            type="link"
                            icon={<SvgIcon name="trash" size={12} />}
                            onClick={() => onDelete?.(row.documentSerialId)}
                        ></Button>
                    </>
                );
            },
        });
    }

    return columns;
}

interface ReviewListProps {
    refresh?: number;
    vendorKeys?: string[];
    onDelete?: () => void;
}

const ReviewList: React.FC<ReviewListProps> = ({ refresh, vendorKeys, onDelete }) => {
    const { me } = useUserState();
    const isMeSuperAdmin = isSuperAdmin(me.roles || []);
    const isMeOnboarding = isOnboarding(me.roles || []);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorInfo, setErrorInfo] = useState({} as Error);
    const [showUpdate, setShowUpdate] = useState(false);
    const [review, setReview] = useState<DocumentReview>();
    const [reload, setReload] = useState(0);

    const loadData = useCallback(
        async (paging: SortFilterPaged<UserEnrichedDocumentReview>, abort?: any) => {
            return await listReviewsWrapper(paging, vendorKeys, abort);
        },
        [vendorKeys]
    );

    const handleErrorClick = (error: Error) => {
        setErrorInfo(error);
        setShowErrorModal(true);
    };

    const handleEdit = (review: DocumentReview) => {
        setReview(review);
        setShowUpdate(true);
    };

    const handleUpdate = async (review: DocumentReview) => {
        await editReview(review);
        setShowUpdate(false);
        reloadData();
    };

    const handleDelete = async (serId: string) => {
        Modal.confirm({
            title: "Remove File?",
            content: "Are you sure you want to remove the file?",
            onOk: async () => {
                await deleteReview(serId);
                onDelete?.();
            },
            okText: "Remove",
        });
    };

    const reloadData = () => {
        setReload((prev) => prev + 1);
    };

    useEffect(() => {
        reloadData();
    }, [refresh]);

    const columns = getColumns(
        isMeSuperAdmin,
        handleErrorClick,
        handleDelete,
        handleEdit,
        isMeOnboarding,
        reloadData
    );
    return (
        <>
            <SortableFilteringTable<UserEnrichedDocumentReview>
                load={loadData}
                size={"small"}
                style={{ minHeight: 500 }}
                columns={columns}
                startingFilter={pager as SortFilterPaged<UserEnrichedDocumentReview>}
                refresh={reload}
            />
            <ErrorInfoModal
                open={showErrorModal}
                error={errorInfo}
                onCancel={() => setShowErrorModal(false)}
            />
            <ReviewUpdateModal
                open={showUpdate}
                review={review}
                onCancel={() => {
                    setShowUpdate(false);
                }}
                onOk={handleUpdate}
            />
        </>
    );
};

export default ReviewList;
